import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../shared/formly/ga-formly.service';
import { FormattingService } from '../../shared/services/formatting.service';
import { MailDataService } from '../mail-data.service';
import { TitleService } from '../../shared/services/title.service';

interface IModel {
  reason:{
    id:string;
    name:string;
  };
  otherReason:string;
}

@Component({
  selector: 'mail-unsubscribe',
  template: `
    <div class="centered mail-settings">
      <h3>{{title}}</h3>
      <div *ngIf="generalError">
        <p class="text-error"><i class="fa fa-exclamation-circle"></i> {{generalError}}</p>
      </div>

      <div *ngIf="unsubscribeError">
        <p class="text-error">
          <i class="fa fa-exclamation-circle"></i>
          Unsubscription has failed. Please try again.
        </p>
        <button class="btn btn-primary" (click)="unsubscribe()" [busyButton]="unsubscribing">Unsubscribe</button>
      </div>

      <div *ngIf="resubscribeSuccess">
        <p class="text-info">You have been re-added successfully to our catalog upload mailing list.</p>
      </div>

      <div *ngIf="submitReasonSuccess">
        <p class="text-info">Thank you for your feedback.</p>
      </div>

      <div *ngIf="unsubscribeSuccess && !resubscribeSuccess && !submitReasonSuccess">
        <p class="text-info">You have been removed successfully from our mailing list and will no longer receive e-mails related to catalog uploads.
          Please note that you will still receive mail for your open orders, quotes and tickets.</p>

        <div class="re-subscribe">
          <div *ngIf="resubscribeError">
            <p class="text-error">
              <i class="fa fa-exclamation-circle"></i>
              Resubscription has failed. Please try again.
            </p>
          </div>
          <div *ngIf="!resubscribeError">
            <p>Did you <strong>unsubscribe by accident?</strong></p>
          </div>
          <button class="btn btn-primary" (click)="resubscribe()" [busyButton]="resubscribing">Re-subscribe</button>
        </div>

        <div class="reason">
          <div *ngIf="submitReasonError">
            <p class="text-error">
              <i class="fa fa-exclamation-circle"></i>
              Something went wrong while submitting your feedback. Please try again.
            </p>
          </div>

          <p class="col-space-bottom">If you have a moment, please let us know <strong>why you unsubscribed</strong>:</p>
          <formly [form]="form" [fields]="fields" [model]="model">
            <div class="controls">
              <button class="btn btn-primary" (click)="submitReason()" [busyButton]="submittingReason" [disabled]="form.invalid">Submit Reason</button>
            </div>
          </formly>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['../mail.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MailUnsubscribeComponent implements OnInit {
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IModel = {
    reason: undefined,
    otherReason: undefined
  };

  public generalError:string;
  public title:string = 'ACSL E-mail';
  public submittingReason:boolean;
  public unsubscribing:boolean;
  public resubscribing:boolean;

  public unsubscribeSuccess:boolean;
  public unsubscribeError:boolean;
  public resubscribeSuccess:boolean;
  public resubscribeError:boolean;
  public submitReasonSuccess:boolean;
  public submitReasonError:boolean;

  @Input() public deconid:string;
  @Input() public token:string;

  private otherReasonField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private mailDataService:MailDataService,
    titleService:TitleService
  ) {
    titleService.setTitle('Newsletter');
  }

  public ngOnInit():void {
    // create fields
    this.fields = this.createFields();

    if (!this.deconid || !this.token) {
      this.generalError = 'Missing parameter!';
    } else {
      this.unsubscribe();
    }
  }

  public unsubscribe():void {
    this.unsubscribing = true;

    this.mailDataService.unsubscribe(this.deconid, this.token)
      .then(() => {
        this.unsubscribeSuccess = true;
        this.unsubscribeError = false;
      })
      .catch(() => {
        this.unsubscribeSuccess = false;
        this.unsubscribeError = true;
      })
      .finally(() => {
        this.unsubscribing = false;
      });
  }

  public resubscribe():void {
    this.resubscribing = true;

    this.mailDataService.resubscribe(this.deconid, this.token)
      .then(() => {
        this.resubscribeSuccess = true;
        this.resubscribeError = false;
      })
      .catch(() => {
        this.resubscribeSuccess = false;
        this.resubscribeError = true;
      })
      .finally(() => {
        this.resubscribing = false;
      });
  }

  public submitReason():void {
    let reason:string = this.model.reason?.id;
    if (this.model.reason?.id === 'otherReason') {
      reason = this.model.otherReason;
    }

    this.submittingReason = true;
    this.mailDataService.unsubscribereason(this.deconid, this.token, reason)
      .then(() => {
        this.submitReasonSuccess = true;
        this.submitReasonError = false;
      })
      .catch(() => {
        this.submitReasonSuccess = false;
        this.submitReasonError = true;
      })
      .finally(() => {
        this.submittingReason = false;
      });
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const reasonOptions:{ id:string; name:string }[] = [
      {
        id: 'doNotWant',
        name: 'I no longer want to receive these emails'
      },
      {
        id: 'noSignup',
        name: 'I never signed up for this mailing list'
      },
      {
        id: 'inappropriate',
        name: 'The emails are inappropriate'
      },
      {
        id: 'otherReason',
        name: 'Other (fill in reason below)'
      }
    ];

    return [
      this.formlyService.aRadio('reason')
        .isRequired()
        .withOptions(reasonOptions)
        .withFormat(this.formattingService.formatName)
        .withoutWrapper()
        .onChange(() => {
          this.otherReasonField.templateOptions.required = this.model?.reason?.id === 'otherReason';
        })
        .build(),

      this.otherReasonField = this.formlyService.aTextarea('otherReason')
        .withHideExpression(() => this.model?.reason?.id !== 'otherReason')
        .withoutWrapper()
        .withRequired(false)
        .withMinLength(5)
        .build()
    ];
  }
}